import { useParams } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import { useContext, useEffect } from "react";
import { Mui, PageHeadingContext } from "@osu/react-ui";
import SummaryInfo from "./SummaryInfo";
import { shallowEqual, useSelector } from "react-redux";
import { selectFormHeadingInfo } from "../../../services/storage/slices";
import {
  selectCurrentFormDataS3Path,
  selectFormIsReadonly,
  selectFormValues,
} from "../../../services/form/slices";
import { changeDocumentTitle } from "../../../_configuration/routing/hooks/usePageTitleFromRouting";
import { useGetSessionInfoQuery } from "../../../services/submissionPeriod/api";
import { formatStartEnd } from "../../../util/functions";
import { format, parseISO } from "date-fns";
import { selectActiveReportingPeriod } from "../../../services/submissionPeriod/slices";
import MetadataList from "./MetadataList";
import useGetFormQueryByParam from "../../hooks/useGetFormQueryByParam";

const spacing = (theme) => ({
  marginBottom: theme.spacing(2),
});

const formatDateHeading = ({
  start,
  end,
  subheading = "",
  description = "",
}) => {
  if (!start || !end) {
    return "";
  }
  const startMonth = format(parseISO(start), "MMMM");
  const endMonth = format(parseISO(end), "MMMM");
  const subheadingRelaysDate =
    subheading.includes(startMonth) && subheading.includes(endMonth);
  const descriptionRelaysDat =
    description.includes(startMonth) && description.includes(endMonth);
  if (subheadingRelaysDate || descriptionRelaysDat) {
    return "";
  }

  return formatStartEnd({
    start,
    end,
  });
};

function Header() {
  const { type, id } = useParams();
  const { setHeading, applicationName, setPageTitle, ...pageHeadingContext } =
    useContext(PageHeadingContext);

  const storedS3Path = useSelector((state) => {
    return id && selectCurrentFormDataS3Path(state, type, id);
  });
  const { year, period } = useSelector((state) => {
    const vals = selectFormValues(state, type, id);
    return {
      year: vals?.year,
      period: vals?.period,
    };
  }, shallowEqual);
  const useCustomSession = !id && year && period;
  const { data } = useGetSessionInfoQuery(
    {
      year,
      period,
    },
    {
      skip: !useCustomSession,
    }
  );

  const { isLoading: formLoading } = useGetFormQueryByParam()
  const selectPageHeadingInformation = (state) => {
    const info = selectFormHeadingInfo(state, {
      s3Url: storedS3Path,
      formId: id,
      formType: type,
    });
    return info;
  };
  const { subheading, description } = useSelector(
    selectPageHeadingInformation,
    shallowEqual
  );

  const dateHeading = useSelector((state) => {
    const parms = {
      subheading,
      description,
    };
    if (id) {
      parms.start = data?.start;
      parms.end = data?.end;
    } else {
      const period = selectActiveReportingPeriod(state);
      parms.start = period?.start;
      parms.end = period?.end;
    }
    return formatDateHeading(parms);
  });

  const selectPageHeading = (state) => {
    const info = selectPageHeadingInformation(state);
    let headingFromAPI = info?.heading;
    if (!headingFromAPI) {
      return "";
    }
    const readOnly = selectFormIsReadonly(state, {
      id,
      type,
    });
    if (readOnly) {
      headingFromAPI += ` (Read Only)`;
    } else if (!id) {
      headingFromAPI = `Create ${headingFromAPI}`;
    } else {
      headingFromAPI = `Edit ${headingFromAPI}`;
    }

    const alreadySet = pageHeadingContext?.heading === headingFromAPI;
    if (headingFromAPI && !alreadySet) {
      return headingFromAPI;
    }
  };
  const headingToSetInContext = useSelector(selectPageHeading);

  useEffect(() => {
    if (headingToSetInContext) {
      setHeading(headingToSetInContext);
      setPageTitle(headingToSetInContext);
      changeDocumentTitle(headingToSetInContext);
    }
  }, [headingToSetInContext, setHeading, setPageTitle]);

  return (
    <Fragment>
      <Mui.Box display="flex" flexDirection="column" component="section">
        {subheading && (
          <Mui.Typography sx={spacing} variant="h2">
            {subheading}
          </Mui.Typography>
        )}
        {dateHeading && (
          <Mui.Typography sx={spacing} variant="h3" component="div">
            {dateHeading}
          </Mui.Typography>
        )}
        {description && (
          <Mui.Typography sx={spacing} variant="body1">
            {description}
          </Mui.Typography>
        )}
        {id && <MetadataList
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          gap={2}
          margin={0}
          padding={2}
          paddingTop={0}
          sx={{
            "& dt": { marginBottom: "0.5px" },
            "& dd": { margin: "0px", marginBottom: "2px" },
          }}
          loading={formLoading}
        />}
        <SummaryInfo />
      </Mui.Box>
    </Fragment>
  );
}

export default Header;
